<template>
  <div class="system-logs-container">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2>系统日志</h2>
      <p>查看和管理系统操作日志记录</p>
    </div>

    <!-- 系统状态卡片 -->
    <div class="status-section">
      <el-row :gutter="20">
        <el-col :span="6">
          <el-card class="status-card">
            <div class="status-content">
              <div class="status-icon" :class="systemStatus.database === 'healthy' ? 'success' : 'error'">
                <el-icon><Database /></el-icon>
              </div>
              <div class="status-info">
                <div class="status-label">数据库状态</div>
                <div class="status-value" :class="systemStatus.database === 'healthy' ? 'success' : 'error'">
                  {{ systemStatus.database === 'healthy' ? '正常' : '异常' }}
                </div>
              </div>
            </div>
          </el-card>
        </el-col>
        
        <el-col :span="6">
          <el-card class="status-card">
            <div class="status-content">
              <div class="status-icon info">
                <el-icon><Timer /></el-icon>
              </div>
              <div class="status-info">
                <div class="status-label">运行时间</div>
                <div class="status-value">{{ formatUptime(systemStatus.uptime) }}</div>
              </div>
            </div>
          </el-card>
        </el-col>
        
        <el-col :span="6">
          <el-card class="status-card">
            <div class="status-content">
              <div class="status-icon warning">
                <el-icon><DataAnalysis /></el-icon>
              </div>
              <div class="status-info">
                <div class="status-label">24小时验证</div>
                <div class="status-value">{{ systemStatus.recent_verifications || 0 }}</div>
              </div>
            </div>
          </el-card>
        </el-col>
        
        <el-col :span="6">
          <el-card class="status-card">
            <div class="status-content">
              <div class="status-icon success">
                <el-icon><Connection /></el-icon>
              </div>
              <div class="status-info">
                <div class="status-label">在线设备</div>
                <div class="status-value">{{ systemStatus.online_devices || 0 }}</div>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 筛选器 -->
    <div class="filter-section">
      <el-card>
        <el-form :model="filters" inline>
          <el-form-item label="操作类型">
            <el-select
              v-model="filters.operation_type"
              placeholder="选择操作类型"
              clearable
              style="width: 180px"
            >
              <el-option label="用户登录" value="login" />
              <el-option label="用户登出" value="logout" />
              <el-option label="卡密管理" value="card_management" />
              <el-option label="代理商管理" value="agent_management" />
              <el-option label="用户管理" value="user_management" />
              <el-option label="系统配置" value="system_config" />
            </el-select>
          </el-form-item>
          
          <el-form-item label="目标类型">
            <el-select
              v-model="filters.target_type"
              placeholder="选择目标类型"
              clearable
              style="width: 150px"
            >
              <el-option label="卡密" value="card" />
              <el-option label="代理商" value="agent" />
              <el-option label="用户" value="user" />
              <el-option label="系统" value="system" />
            </el-select>
          </el-form-item>
          
          <el-form-item label="操作者">
            <el-input
              v-model="filters.operator"
              placeholder="输入操作者"
              clearable
              style="width: 150px"
            />
          </el-form-item>
          
          <el-form-item label="时间范围">
            <el-date-picker
              v-model="filters.dateRange"
              type="datetimerange"
              range-separator="至"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              format="YYYY-MM-DD HH:mm:ss"
              value-format="YYYY-MM-DD HH:mm:ss"
              style="width: 350px"
            />
          </el-form-item>
          
          <el-form-item>
            <el-button type="primary" @click="searchLogs" :loading="loading">
              <el-icon><Search /></el-icon>
              搜索
            </el-button>
            <el-button @click="resetFilters">
              <el-icon><Refresh /></el-icon>
              重置
            </el-button>
            <el-button @click="refreshStatus">
              <el-icon><RefreshRight /></el-icon>
              刷新状态
            </el-button>
          </el-form-item>
        </el-form>
      </el-card>
    </div>

    <!-- 操作日志表格 -->
    <div class="table-section">
      <el-card>
        <template #header>
          <div class="card-header">
            <span>操作日志列表</span>
            <el-button type="primary" size="small" @click="refreshLogs">
              <el-icon><Refresh /></el-icon>
              刷新
            </el-button>
          </div>
        </template>
        
        <el-table
          :data="logs"
          v-loading="loading"
          stripe
          style="width: 100%"
          :default-sort="{ prop: 'created_at', order: 'descending' }"
        >
          <el-table-column prop="id" label="ID" width="80" />
          
          <el-table-column prop="operator_name" label="操作者" width="120">
            <template #default="scope">
              <div class="operator-info">
                <el-tag :type="getOperatorTypeColor(scope.row.operator_type)" size="small">
                  {{ getOperatorTypeText(scope.row.operator_type) }}
                </el-tag>
                <div class="operator-name">{{ scope.row.operator_name || '-' }}</div>
              </div>
            </template>
          </el-table-column>
          
          <el-table-column prop="operation_type" label="操作类型" width="150">
            <template #default="scope">
              <el-tag :type="getOperationTypeColor(scope.row.operation_type)" size="small">
                {{ getOperationTypeText(scope.row.operation_type) }}
              </el-tag>
            </template>
          </el-table-column>
          
          <el-table-column prop="operation_desc" label="操作描述" min-width="200">
            <template #default="scope">
              <el-text truncated>{{ scope.row.operation_desc }}</el-text>
            </template>
          </el-table-column>
          
          <el-table-column prop="target_type" label="目标类型" width="100">
            <template #default="scope">
              <el-tag v-if="scope.row.target_type" type="info" size="small">
                {{ getTargetTypeText(scope.row.target_type) }}
              </el-tag>
              <span v-else>-</span>
            </template>
          </el-table-column>
          
          <el-table-column prop="ip_address" label="IP地址" width="140" />
          
          <el-table-column prop="created_at" label="操作时间" width="180">
            <template #default="scope">
              {{ formatDateTime(scope.row.created_at) }}
            </template>
          </el-table-column>
          
          <el-table-column label="操作" width="120" fixed="right">
            <template #default="scope">
              <el-button
                type="primary"
                size="small"
                @click="showLogDetail(scope.row)"
              >
                详情
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        
        <!-- 分页 -->
        <div class="pagination-wrapper">
          <el-pagination
            v-model:current-page="pagination.page"
            v-model:page-size="pagination.limit"
            :page-sizes="[10, 20, 50, 100]"
            :total="pagination.total"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </el-card>
    </div>

    <!-- 日志详情对话框 -->
    <el-dialog
      v-model="detailDialogVisible"
      title="操作日志详情"
      width="800px"
      :close-on-click-modal="false"
    >
      <div v-if="selectedLog" class="log-detail">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="日志ID">{{ selectedLog.id }}</el-descriptions-item>
          <el-descriptions-item label="操作者">
            <div class="operator-detail">
              <el-tag :type="getOperatorTypeColor(selectedLog.operator_type)" size="small">
                {{ getOperatorTypeText(selectedLog.operator_type) }}
              </el-tag>
              <span class="operator-name">{{ selectedLog.operator_name || '-' }}</span>
            </div>
          </el-descriptions-item>
          <el-descriptions-item label="操作类型">
            <el-tag :type="getOperationTypeColor(selectedLog.operation_type)" size="small">
              {{ getOperationTypeText(selectedLog.operation_type) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="目标类型">
            <el-tag v-if="selectedLog.target_type" type="info" size="small">
              {{ getTargetTypeText(selectedLog.target_type) }}
            </el-tag>
            <span v-else>-</span>
          </el-descriptions-item>
          <el-descriptions-item label="目标ID">{{ selectedLog.target_id || '-' }}</el-descriptions-item>
          <el-descriptions-item label="IP地址">{{ selectedLog.ip_address || '-' }}</el-descriptions-item>
          <el-descriptions-item label="操作时间" :span="2">{{ formatDateTime(selectedLog.created_at) }}</el-descriptions-item>
        </el-descriptions>
        
        <div class="detail-section">
          <h4>操作描述</h4>
          <el-input
            type="textarea"
            :value="selectedLog.operation_desc"
            :rows="2"
            readonly
          />
        </div>
        
        <div class="detail-section" v-if="selectedLog.user_agent">
          <h4>用户代理</h4>
          <el-input
            type="textarea"
            :value="selectedLog.user_agent"
            :rows="2"
            readonly
          />
        </div>
        
        <div class="detail-section" v-if="selectedLog.old_data">
          <h4>变更前数据</h4>
          <el-input
            type="textarea"
            :value="formatJSON(selectedLog.old_data)"
            :rows="5"
            readonly
          />
        </div>
        
        <div class="detail-section" v-if="selectedLog.new_data">
          <h4>变更后数据</h4>
          <el-input
            type="textarea"
            :value="formatJSON(selectedLog.new_data)"
            :rows="5"
            readonly
          />
        </div>
      </div>
      
      <template #footer>
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { logAPI } from '@/services/api'

export default {
  name: 'SystemLogs',
  setup() {
    const loading = ref(false)
    const logs = ref([])
    const systemStatus = ref({})
    const detailDialogVisible = ref(false)
    const selectedLog = ref(null)

    const filters = reactive({
      operation_type: '',
      target_type: '',
      operator: '',
      dateRange: null
    })

    const pagination = reactive({
      page: 1,
      limit: 20,
      total: 0
    })

    // 获取操作日志
    const fetchLogs = async () => {
      try {
        loading.value = true
        const params = {
          page: pagination.page,
          limit: pagination.limit,
          ...filters
        }

        if (filters.dateRange && filters.dateRange.length === 2) {
          params.start_date = filters.dateRange[0]
          params.end_date = filters.dateRange[1]
        }

        const response = await logAPI.getOperationLogs(params)
        if (response.success) {
          logs.value = response.data.logs
          pagination.total = response.data.pagination.total
        }
      } catch (error) {
        console.error('获取操作日志失败:', error)
        ElMessage.error('获取操作日志失败')
      } finally {
        loading.value = false
      }
    }

    // 获取系统状态
    const fetchSystemStatus = async () => {
      try {
        const response = await logAPI.getSystemStatus()
        if (response.success) {
          systemStatus.value = response.data
        }
      } catch (error) {
        console.error('获取系统状态失败:', error)
      }
    }

    // 搜索日志
    const searchLogs = () => {
      pagination.page = 1
      fetchLogs()
    }

    // 重置筛选器
    const resetFilters = () => {
      Object.keys(filters).forEach(key => {
        filters[key] = key === 'dateRange' ? null : ''
      })
      pagination.page = 1
      fetchLogs()
    }

    // 刷新日志
    const refreshLogs = () => {
      fetchLogs()
    }

    // 刷新状态
    const refreshStatus = () => {
      fetchSystemStatus()
      ElMessage.success('系统状态已刷新')
    }

    // 显示日志详情
    const showLogDetail = (log) => {
      selectedLog.value = log
      detailDialogVisible.value = true
    }

    // 分页处理
    const handleSizeChange = (size) => {
      pagination.limit = size
      pagination.page = 1
      fetchLogs()
    }

    const handleCurrentChange = (page) => {
      pagination.page = page
      fetchLogs()
    }

    // 工具函数
    const formatDateTime = (dateTime) => {
      if (!dateTime) return '-'
      return new Date(dateTime).toLocaleString('zh-CN')
    }

    const formatUptime = (seconds) => {
      if (!seconds) return '-'
      const days = Math.floor(seconds / 86400)
      const hours = Math.floor((seconds % 86400) / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      return `${days}天 ${hours}小时 ${minutes}分钟`
    }

    const formatJSON = (jsonString) => {
      try {
        return JSON.stringify(JSON.parse(jsonString), null, 2)
      } catch {
        return jsonString
      }
    }

    const getOperatorTypeColor = (type) => {
      const colors = {
        'admin': 'danger',
        'agent': 'warning',
        'user': 'info'
      }
      return colors[type] || 'info'
    }

    const getOperatorTypeText = (type) => {
      const texts = {
        'admin': '管理员',
        'agent': '代理商',
        'user': '用户'
      }
      return texts[type] || type
    }

    const getOperationTypeColor = (type) => {
      const colors = {
        'login': 'success',
        'logout': 'info',
        'card_management': 'primary',
        'agent_management': 'warning',
        'user_management': 'danger',
        'system_config': 'info'
      }
      return colors[type] || 'info'
    }

    const getOperationTypeText = (type) => {
      const texts = {
        'login': '登录',
        'logout': '登出',
        'card_management': '卡密管理',
        'agent_management': '代理商管理',
        'user_management': '用户管理',
        'system_config': '系统配置'
      }
      return texts[type] || type
    }

    const getTargetTypeText = (type) => {
      const texts = {
        'card': '卡密',
        'agent': '代理商',
        'user': '用户',
        'system': '系统'
      }
      return texts[type] || type
    }

    onMounted(() => {
      fetchLogs()
      fetchSystemStatus()
    })

    return {
      loading,
      logs,
      systemStatus,
      filters,
      pagination,
      detailDialogVisible,
      selectedLog,
      fetchLogs,
      searchLogs,
      resetFilters,
      refreshLogs,
      refreshStatus,
      showLogDetail,
      handleSizeChange,
      handleCurrentChange,
      formatDateTime,
      formatUptime,
      formatJSON,
      getOperatorTypeColor,
      getOperatorTypeText,
      getOperationTypeColor,
      getOperationTypeText,
      getTargetTypeText
    }
  }
}
</script>

<style scoped>
.system-logs-container {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0 0 8px 0;
  color: #303133;
}

.page-header p {
  margin: 0;
  color: #909399;
  font-size: 14px;
}

.status-section {
  margin-bottom: 20px;
}

.status-card {
  height: 100px;
}

.status-content {
  display: flex;
  align-items: center;
  height: 100%;
}

.status-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  font-size: 24px;
  color: white;
}

.status-icon.success {
  background: linear-gradient(135deg, #67c23a, #85ce61);
}

.status-icon.error {
  background: linear-gradient(135deg, #f56c6c, #f78989);
}

.status-icon.info {
  background: linear-gradient(135deg, #409eff, #66b1ff);
}

.status-icon.warning {
  background: linear-gradient(135deg, #e6a23c, #ebb563);
}

.status-info {
  flex: 1;
}

.status-label {
  font-size: 14px;
  color: #909399;
  margin-bottom: 4px;
}

.status-value {
  font-size: 18px;
  font-weight: bold;
  color: #303133;
}

.status-value.success {
  color: #67c23a;
}

.status-value.error {
  color: #f56c6c;
}

.filter-section {
  margin-bottom: 20px;
}

.table-section {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.operator-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.operator-name {
  font-size: 12px;
  color: #606266;
}

.operator-detail {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-wrapper {
  margin-top: 20px;
  text-align: right;
}

.log-detail {
  max-height: 600px;
  overflow-y: auto;
}

.detail-section {
  margin-top: 20px;
}

.detail-section h4 {
  margin: 0 0 10px 0;
  color: #303133;
  font-size: 14px;
}
</style>
